/**
 * Nome curto de unidade de saude — usado no seletor "Unidade de Saude" do menu
 * lateral, onde a caixa tem ~208px uteis e os nomes oficiais chegam a 59
 * caracteres ("Centro de Atencao Integral A Saude Cantidio de Moura Campos").
 *
 * O nome completo continua sendo exibido no tooltip, na lista do seletor e nas
 * telas do /saude — a abreviacao e apenas de apresentacao.
 */

/** Conectores ignorados na formacao de siglas e removidos quando sobram soltos. */
const CONECTORES = new Set(['de', 'da', 'do', 'dos', 'das', 'e', 'em', 'a', 'ao', 'as', 'os', '-']);

/** Siglas que devem aparecer em maiusculas quando abrem o nome. */
const SIGLAS = new Set([
  'ubs', 'usf', 'ubsf', 'esf', 'psf', 'cs', 'us', 'caps', 'ceo', 'ame', 'upa', 'pa',
  'cais', 'cerest', 'sms', 'hc', 'samu', 'ciatox', 'sep', 'ps', 'crst', 'nasf',
]);

/** Prefixos longos -> sigla. Ordem importa: mais especifico primeiro. */
const PREFIXOS: Array<{ termos: string; sigla: string }> = [
  { termos: 'unidade basica de saude da familia', sigla: 'UBSF' },
  { termos: 'unidade basica de saude', sigla: 'UBS' },
  { termos: 'unidade de saude da familia', sigla: 'USF' },
  { termos: 'estrategia de saude da familia', sigla: 'ESF' },
  { termos: 'estrategia saude da familia', sigla: 'ESF' },
  { termos: 'programa de saude da familia', sigla: 'PSF' },
  { termos: 'centro de atencao integral a saude', sigla: 'CAIS' },
  { termos: 'centro de atencao psicossocial', sigla: 'CAPS' },
  { termos: 'centro de especialidades odontologicas', sigla: 'CEO' },
  { termos: 'centro de referencia em saude do trabalhador', sigla: 'CEREST' },
  { termos: 'centro de saude', sigla: 'CS' },
  { termos: 'ambulatorio medico de especialidades', sigla: 'AME' },
  { termos: 'unidade de pronto atendimento', sigla: 'UPA' },
  { termos: 'pronto atendimento', sigla: 'PA' },
  { termos: 'pronto socorro', sigla: 'PS' },
  { termos: 'secretaria municipal de saude', sigla: 'SMS' },
  { termos: 'hospital das clinicas', sigla: 'HC' },
  { termos: 'unidade de saude', sigla: 'US' },
];

/** Minusculas, sem acento, espacos colapsados. */
export function fold(texto: string): string {
  return (texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function tokens(texto: string): string[] {
  return (texto || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
}

/**
 * Sufixo repetido na lista de unidades do municipio (ex. ["botucatu"] ou
 * ["de", "nova", "serrana"]), devolvido em tokens folded. Serve para tirar do
 * nome curto a parte que nao distingue nada — todas as unidades a tem.
 *
 * Regra: dos ultimos 1, 2 ou 3 tokens, vence o maior (em numero de tokens) que
 * seja sufixo de pelo menos metade dos nomes. O nome do municipio ativo entra
 * como candidato mesmo abaixo desse limite (o cadastro do PEC pode abreviar a
 * cidade em parte das unidades).
 */
export function sufixoComumUnidades(nomes: string[], municipioNome?: string): string[] {
  const listas = (nomes || []).map((n) => tokens(fold(n))).filter((t) => t.length > 1);
  const candidatos = new Map<string, string[]>();

  if (listas.length >= 4) {
    const minimo = Math.ceil(listas.length / 2);
    for (const toks of listas) {
      for (let tamanho = 1; tamanho <= 3 && tamanho < toks.length; tamanho++) {
        const cand = toks.slice(toks.length - tamanho);
        const chave = cand.join(' ');
        if (candidatos.has(chave)) continue;
        const ocorrencias = listas.filter((outra) => {
          if (outra.length <= tamanho) return false;
          return outra.slice(outra.length - tamanho).join(' ') === chave;
        }).length;
        if (ocorrencias >= minimo) candidatos.set(chave, cand);
      }
    }
  }

  const municipio = tokens(fold(municipioNome || ''));
  if (municipio.length > 0 && municipio.length <= 3) {
    candidatos.set(municipio.join(' '), municipio);
  }

  let melhor: string[] = [];
  candidatos.forEach((cand) => {
    if (cand.length > melhor.length) melhor = cand;
  });
  return melhor;
}

function removerSufixo(toks: string[], sufixo: string[]): string[] {
  if (sufixo.length === 0 || toks.length <= sufixo.length) return toks;
  const fim = toks.slice(toks.length - sufixo.length).map(fold).join(' ');
  if (fim !== sufixo.join(' ')) return toks;

  let restante = toks.slice(0, toks.length - sufixo.length);
  // conector que ficou pendurado no fim ("... do Amaral de" -> "... do Amaral")
  while (restante.length > 0 && CONECTORES.has(fold(restante[restante.length - 1]))) {
    restante = restante.slice(0, -1);
  }
  return restante.length > 0 ? restante : toks;
}

function aplicarPrefixo(toks: string[]): string[] {
  const folded = toks.map(fold);
  for (const { termos, sigla } of PREFIXOS) {
    const alvo = termos.split(' ');
    if (folded.length < alvo.length) continue;
    if (folded.slice(0, alvo.length).join(' ') === termos) {
      return [sigla, ...toks.slice(alvo.length)];
    }
  }
  return toks;
}

/**
 * "Cerest Centro de Referencia em Saude do Trabalhador" -> "CEREST": o nome ja
 * abre com a sigla e repete a expansao logo depois.
 */
function removerExpansaoConhecida(toks: string[]): string[] {
  if (toks.length < 2) return toks;
  const sigla = fold(toks[0]);
  const folded = toks.slice(1).map(fold);
  for (const item of PREFIXOS) {
    if (item.sigla.toLowerCase() !== sigla) continue;
    const alvo = item.termos.split(' ');
    if (folded.length < alvo.length) continue;
    if (folded.slice(0, alvo.length).join(' ') === item.termos) {
      return [toks[0], ...toks.slice(alvo.length + 1)];
    }
  }
  return toks;
}

/**
 * "Ceo Centro de Especialidades Odontologicas" -> "CEO": quando o primeiro
 * token e a sigla dos tokens seguintes, a expansao e redundante.
 */
function removerExpansaoRedundante(toks: string[]): string[] {
  if (toks.length < 3) return toks;
  const sigla = fold(toks[0]);
  if (!/^[a-z]{2,7}$/.test(sigla)) return toks;

  for (let k = Math.min(6, toks.length - 1); k >= 2; k--) {
    const iniciais = toks
      .slice(1, k + 1)
      .map(fold)
      .filter((t) => !CONECTORES.has(t))
      .map((t) => t[0])
      .join('');
    if (iniciais === sigla) return [toks[0], ...toks.slice(k + 1)];
  }
  return toks;
}

function normalizarSigla(toks: string[]): string[] {
  if (toks.length === 0) return toks;
  const primeiro = fold(toks[0]);
  if (SIGLAS.has(primeiro)) return [primeiro.toUpperCase(), ...toks.slice(1)];
  return toks;
}

/**
 * Nome curto para exibicao. Sem corte por numero de caracteres — o corte fica
 * com o CSS (text-overflow: ellipsis), que se adapta a largura real do campo.
 */
export function abreviarUnidadeNome(nome: string, sufixo: string[] = []): string {
  const original = (nome || '').replace(/\s+/g, ' ').trim();
  if (!original) return '';

  let toks = tokens(original);
  toks = removerSufixo(toks, sufixo);
  toks = aplicarPrefixo(toks);
  toks = removerExpansaoConhecida(toks);
  toks = removerExpansaoRedundante(toks);
  toks = normalizarSigla(toks);

  const curto = toks.join(' ').trim();
  return curto || original;
}
